import { select } from "gamedata/GameData";
import invariant from "tiny-invariant";
import { ItemLink } from "~/components/item/ItemLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Icon } from "~/components/ui/Icon";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { tt, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE } from "~/util/Loaders";
import { ReactNode, useState } from "react";
import { AdenaValue } from "~/components/item/Adena";
import { Input } from "~/components/form/Input";
import { homePage } from "~/util/titles";
import { gameName } from "~/util/common";
import { logPageView } from "~/util/stats";

const RECIPE_IDS_C2 = [
  "rp_soulshot_d",
  "rp_spiritshot_d",
  "rp_blessed_spiritshot_d",
  "rp_soulshot_c",
  "rp_spiritshot_c",
  "rp_blessed_spiritshot_c",
  "rp_soulshot_b",
  "rp_spiritshot_b",
  "rp_blessed_spiritshot_b",
];
const RECIPE_IDS_C3 = RECIPE_IDS_C2.concat([
  "rp_soulshot_a",
  "rp_spiritshot_a",
  "rp_blessed_spiritshot_a",
]);

const RECIPE_IDS_C4 = RECIPE_IDS_C3.concat([
  "rp_soulshot_s",
  "rp_spiritshot_s",
  "rp_blessed_spiritshot_s",
]);

$PAGE({
  path: "craft/shots",

  loader: $LOADER?.(async (cx) => {
    // Log
    logPageView(cx);
    //
    const t = tt.bind(null, cx.realm.lang);
    let RECIPE_IDS: string[] = [];
    switch (cx.realm.game) {
      case "c1":
      case "c2":
        RECIPE_IDS = RECIPE_IDS_C2;
        break;
      case "c3":
        RECIPE_IDS = RECIPE_IDS_C3;
        break;
      default:
        RECIPE_IDS = RECIPE_IDS_C4;
        break;
    }

    const recipes = RECIPE_IDS.map((id) => {
      let recipe = cx.gd.items.get(id);
      invariant(recipe, () => `Recipe '${id}' not found.`);
      return recipe;
    });

    return {
      authUser: cx.req.sess?.user,
      recipes: select(recipes, {
        select: {
          id: true,
          itemName: true,
          name: t,
          icon: true,
          defaultPrice: true,
          recipe: {
            select: {
              mpConsume: true,
              productList: {
                select: {
                  count: true,
                  item: {
                    id: true,
                    name: t,
                    itemName: true,
                    icon: true,
                    defaultPrice: true,
                  },
                },
              },
              materialList: {
                select: {
                  count: true,
                  item: {
                    id: true,
                    itemName: true,
                    name: t,
                    icon: true,
                    defaultPrice: true,
                  },
                },
              },
            },
          },
        },
      }),
    };
  }),

  meta: (cx) => {
    const title = "Shots Craft Calculator";
    const game = gameName(cx.realm.game);

    return {
      title: `${title} - ${homePage(cx.realm)}`,
      description: cx.t({
        en: `Profit calculator for crafting Soulshots and Spiritshots ${game}.`,
        ru: `Калькулятор прибыли для создания Soulshots и Spiritshots ${game}.`,
      }),
    };
  },

  render: ({ data }) => {
    const turl = useTurl();

    const breadcrumbList = [
      {
        title: "Shots Craft Calculator",
        url: turl(`/craft/shots`),
      },
    ];

    const [prices, setPrices] = useState<Record<number, number>>({});

    function getPrice(item: { id: number; defaultPrice: number | null }) {
      return (prices[item.id] ?? item.defaultPrice) || 0;
    }

    return (
      <div>
        <div className="content">
          <Breadcrumb items={breadcrumbList} />
          <PageTitle>Shots Craft Calculator</PageTitle>

          <div>
            This calculator helps you to estimate profit from crafting and
            selling Spiritshots and Soulshots.
          </div>
        </div>

        <Table
          data={data.recipes}
          id={(recipe) => recipe.id}
          cols={[
            {
              title: "Soulshot/Spiritshot",
              cell: (recipe) => (
                <ItemLink item={recipe.recipe[0].productList[0].item} />
              ),
            },
            {
              title: "Ore Price",
              align: "right",
              cell: (recipe) => {
                const mat = recipe.recipe[0].materialList[0];
                return (
                  <Aligned>
                    <IconWithCount count={mat.count} icon={mat.item.icon} />
                    <Input
                      size={5}
                      value={getPrice(mat.item)}
                      onChange={(e) =>
                        setPrices((p) => ({
                          ...p,
                          [mat.item.id]: parseInt(e.target.value) ?? 0,
                        }))
                      }
                    />
                  </Aligned>
                );
              },
            },
            {
              title: "Crystal Price",
              align: "right",
              cell: (recipe) => {
                const mat = recipe.recipe[0].materialList[1];
                return (
                  <Aligned>
                    <IconWithCount count={mat.count} icon={mat.item.icon} />
                    <Input
                      size={5}
                      value={getPrice(mat.item)}
                      onChange={(e) =>
                        setPrices((p) => ({
                          ...p,
                          [mat.item.id]: parseInt(e.target.value) ?? 0,
                        }))
                      }
                    />
                  </Aligned>
                );
              },
            },
            {
              title: "Sell Price",
              align: "right",
              cell: (recipe) => {
                const prod = recipe.recipe[0].productList[0];
                return (
                  <Aligned>
                    <Icon icon={prod.item.icon} size={24} />
                    <Input
                      size={5}
                      value={getPrice(prod.item)}
                      onChange={(e) =>
                        setPrices((p) => ({
                          ...p,
                          [prod.item.id]: parseInt(e.target.value) ?? 0,
                        }))
                      }
                    />
                  </Aligned>
                );
              },
            },
            {
              title: "Count/MP",
              align: "right",
              cell: (recipe) => {
                const prod = recipe.recipe[0].productList[0];
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 2,
                    }}
                  >
                    <div>
                      <IconWithCount count={prod.count} icon={prod.item.icon} />
                    </div>
                    <div
                      style={{
                        color: "#78a8ff",
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {recipe.recipe[0].mpConsume} MP
                    </div>
                  </div>
                );
              },
            },
            {
              title: "Profit",
              align: "right",
              cell: (recipe) => {
                const ore = recipe.recipe[0].materialList[0];
                const crystal = recipe.recipe[0].materialList[1];
                const prod = recipe.recipe[0].productList[0];

                const orePrice = getPrice(ore.item) * ore.count;
                const crystalPrice = getPrice(crystal.item) * crystal.count;
                const revenue = getPrice(prod.item) * prod.count;

                return <AdenaValue value={revenue - orePrice - crystalPrice} />;
              },
            },
          ]}
        />
      </div>
    );
  },
});

function IconWithCount(props: { count: number; icon: string }) {
  return (
    <div style={{ display: "flex", gap: 3 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>x{props.count}</div>
      <Icon icon={props.icon} size={16} />
    </div>
  );
}

function Aligned(props: { children: ReactNode }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {props.children}
    </div>
  );
}
